import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  useGetShop,
  getGetShopQueryKey,
} from '@/api/generated/v2-shop/v2-shop'
import type { ShopResponse as OrvalShopResponse } from '@/api/schemas'
import { FLB_PARTNER_GUIDE_ANCHOR_ID, usePartnerStatus } from '@/features/setup/FlbPartnerShareStatus'
import { MetaFLBCard } from '@/features/setup/MetaFLBCard'
import { MetaManualCard } from '@/features/setup/MetaManualCard'
import { MetaPagesCard } from '@/features/setup/MetaPagesCard'
import { SetupPage } from '@/features/setup/SetupPage'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { CopyableId } from '@/components/guide/CopyableId'
import { GuideStep } from '@/components/guide/GuideStep'
import { TipBox } from '@/components/guide/TipBox'
import { auth } from '@/lib/auth'
import { useQueryClient } from '@tanstack/react-query'

/**
 * 광고계정 표시 라벨 — 캐시된 실제 이름이 있으면 "이름 (act_id)", 없으면 "act_id".
 * meta_ad_account_name 은 orval 재생성 전이라 generated 타입엔 없을 수 있어 옵셔널로 read.
 */
function buildAdAccountInfo(shop: {
  meta_ad_account_id?: string | null
  meta_ad_account_name?: string | null
}): string | undefined {
  if (!shop.meta_ad_account_id) return undefined
  const name = shop.meta_ad_account_name
  return name
    ? `광고 계정: ${name} (act_${shop.meta_ad_account_id})`
    : `광고 계정: act_${shop.meta_ad_account_id}`
}

export const Route = createFileRoute('/_dashboard/setup/meta-ads')({
  component: MetaAdsSetupPage,
})

function MetaAdsSetupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // --- Orval Query 훅: Shop 정보 조회 ---
  const { data: shopRaw, isLoading: loading } = useGetShop({
    query: { enabled: auth.isAuthenticated() },
  })
  const shop = (shopRaw ?? null) as OrvalShopResponse | null

  // --- Hook 순서 보장: 모든 useState를 early return 앞에 배치 ---
  // 기본 탭은 간편 연동(FLB). 레거시도 간편연동 탭을 먼저 보여줘 전환을 유도한다
  // (직접 입력은 탭 클릭으로 접근하는 관리용).
  const [activeTab, setActiveTab] = useState('flb')

  // 인증 안 된 경우 리다이렉트
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate({ to: '/' })
    }
  }, [navigate])

  // 파트너 공유 상태 — 미등록이면 가이드를 자동으로 펼친다.
  // 단 토큰 회수(code190) 상태에선 재연동이 우선이라 배너·자동 펼침을 하지 않는다.
  const flbAdsConnected =
    !!shop?.meta_flb_ads_connected && !!shop?.meta_ad_account_id
  const flbAdsTokenInvalid = Boolean(
    (shop as { meta_flb_ads_token_invalid?: boolean } | null)
      ?.meta_flb_ads_token_invalid,
  )
  const { data: partnerStatus } = usePartnerStatus('ad_account', flbAdsConnected)
  const partnerNotRegistered =
    partnerStatus?.reason === 'not_registered' && !flbAdsTokenInvalid

  if (loading || !shop) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-outline-variant" />
      </div>
    )
  }

  // 연동 상태: FLB 토큰 기반으로 판별
  const isFLBConnected = !!shop.meta_flb_ads_connected && !!shop.meta_ad_account_id
  // FLB 토큰 회수(code190) 감지 — 연동됨이어도 수집 실패 중 (재연동 배너).
  // orval 스펙 재생성 보류 중이라 로컬 타입 보강으로 읽는다.
  const flbTokenInvalid = Boolean(
    (shop as { meta_flb_ads_token_invalid?: boolean }).meta_flb_ads_token_invalid,
  )
  // v1 레거시: FLB 토큰 없이 자산 ID만 있는 상태
  const isLegacy = !shop.meta_flb_ads_connected && !!shop.meta_ad_account_id
  // SetupPage에서 보여줄 전체 연동 상태 (v1이든 v2든 동작 중이면 연결됨)
  const isConnected = isFLBConnected || isLegacy

  // 캠퍼스 통합 이후 광고 계정만 단독 연동하는 케이스가 있어 Mixpanel 선행 조건은 두지 않는다.
  // 광고 데이터 자동 수집은 백엔드에서 Mixpanel 토큰이 없으면 자동으로 skip 한다.
  // (`backend/src/v2/ad_insights/service.py` 참고)

  // 외부 링크
  const externalLink = shop.meta_ad_account_id
    ? `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${shop.meta_ad_account_id}`
    : undefined

  /** FLB / 수동 카드 onSuccess 콜백 — Shop 쿼리 무효화로 리페치 */
  const handleSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
  }

  // 탭에 맞춰 가이드 전환 (SetupPage 2컬럼: 가이드=좌 55% / 카드=우 45%)
  //  - 직접 입력: 파트너 권한 부여 + ID 입력 가이드
  //  - 간편 연동: 메타 로그인 안내 + (선택) 파트너 등록 안내
  const guide =
    activeTab === 'manual' ? <MetaAdsManualGuide /> : <MetaAdsFLBGuide />

  // 수동 입력 카드 + 페이지 공유 카드 — 레거시 탭 흐름에서 사용한다.
  // 페이지 공유는 광고계정 연동과 별개의 옵셔널 자산이라 manual 흐름에서 항상 함께 노출한다.
  const manualCard = (
    <div className="space-y-4">
      <MetaManualCard
        purpose="ads"
        isConnected={isConnected}
        connectedInfo={isConnected ? buildAdAccountInfo(shop) : undefined}
        onSuccess={handleSuccess}
        shop={shop}
        // Shop 응답에 실려 오는 현재 걸린 광고 계정 승인 요청.
        // pending 상태이면 카드가 "승인 대기 중" 뷰를 우선 보여준다.
        pendingRequest={shop.meta_ads_pending_request ?? null}
      />
      {/* Facebook 페이지 공유 (옵셔널·멀티) — 광고 부스트 영상 분석에 필요 */}
      <MetaPagesCard shop={shop} onSuccess={handleSuccess} />
    </div>
  )

  // 간편 연동 (FLB) 카드 — 신규/미연동 샵은 이 카드만 단독으로 노출한다.
  // 레거시(SU) 샵은 아래 Tabs 의 '간편 연동' 탭 안에 동일 카드가 들어간다.
  // 페이지 공유 카드는 여기엔 없다 — FLB(캠퍼스 pxon OAuth) 유저는 pxon이
  // 자체 OAuth 토큰(pages_* scopes)으로 페이지에 접근하므로 솔루션 BM
  // 공유가 불필요하다. 직접 입력(수동/shared_su) 탭에만 유지.
  const flbCard = (
    <MetaFLBCard
      purpose="ads"
      title="광고 계정"
      description="메타 광고 계정을 연결하면 광고비와 성과 데이터를 자동으로 수집해요"
      isConnected={isFLBConnected}
      connectedInfo={isFLBConnected ? buildAdAccountInfo(shop) : undefined}
      legacyConnected={isLegacy}
      legacyInfo={isLegacy ? buildAdAccountInfo(shop) : undefined}
      tokenInvalid={flbTokenInvalid}
      onSuccess={handleSuccess}
    />
  )

  return (
    <SetupPage
      title="메타 광고 연동"
      connected={isConnected}
      externalLink={externalLink}
      externalLinkLabel="메타 광고 관리자"
      guide={guide}
      forceGuideOpen={partnerNotRegistered}
      warningBadge={partnerNotRegistered ? '파트너 공유 필요' : undefined}
      bare
    >
      {/* 레거시(SU 직접입력) 샵만 탭 2개(직접입력 관리 + FLB 전환)를 노출한다.
          그 외 신규/FLB 샵은 탭 없이 FLB(간편 연동) 카드만 단독 노출 —
          직접 입력은 레거시 전용이라 신규에게는 진입점 자체를 숨긴다.
          (manual 카드/탭 코드는 나중에 되살릴 수 있게 그대로 보존) */}
      {isLegacy ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="flb">간편 연동</TabsTrigger>
            <TabsTrigger value="manual">직접 입력</TabsTrigger>
          </TabsList>

          {/* 간편 연동 (FLB) — 메타 로그인 한 번으로 연동 */}
          <TabsContent value="flb">{flbCard}</TabsContent>

          {/* 직접 입력 (수동) */}
          <TabsContent value="manual">{manualCard}</TabsContent>
        </Tabs>
      ) : (
        // 신규/FLB 샵 — 탭 없이 간편 연동 카드만 단독 노출
        flbCard
      )}
    </SetupPage>
  )
}

// ---------------------------------------------------------------------------
// 직접 입력 탭 전용 가이드
// ---------------------------------------------------------------------------

/** 메타 광고 직접 입력 가이드 */
function MetaAdsManualGuide() {
  return (
    <div className="space-y-2">
      <GuideStep number={1} title="Meta 파트너 권한 부여">
        <p>
          메타 비즈니스 설정 → 사용자 → 파트너로 이동해요.
        </p>
        <ol className="list-decimal list-inside space-y-1.5 text-sm mt-2">
          <li>
            Insight Wave를 파트너로 추가해요.
            {/* 진입 경로 분기 — 다른 연동에서 이미 파트너를 추가했을 수 있다 */}
            <ul className="list-disc list-outside pl-6 space-y-1.5 mt-1.5">
              <li>
                처음 연동하는 경우: <strong>추가</strong> → <strong>파트너에게 자산에 대한 액세스 권한 부여</strong> 선택
                → 파트너 비즈니스 ID에 <CopyableId value="1081832460400655" /> 입력 후 다음
              </li>
              <li>
                이미 Insight Wave가 파트너 목록에 있는 경우: 목록에서 <strong>Insight Wave</strong> 선택
                → <strong>자산 할당</strong> 버튼 클릭
              </li>
            </ul>
          </li>
          <li>자산 및 권한 할당에서 <strong>광고 계정</strong> 선택 → <strong>전체 관리 권한</strong> 토글 켜기</li>
          <li>
            <strong>Facebook 페이지</strong>도 함께 공유해주세요{' '}
            <span className="text-on-surface-variant">(광고 영상 분석 시 필요해요)</span>
            <ul className="list-disc list-outside pl-6 space-y-1.5 mt-1.5">
              <li>
                <strong>Facebook Pages</strong> 선택 → 운영 중인 페이지 체크 → 전체 관리 권한의{' '}
                <strong>전체(민감한 작업 제외)</strong> 토글 켜기 → 자산 할당
              </li>
            </ul>
          </li>
          <li>"Insight Wave에서 내 비즈니스의 대행사 역할을 함" 체크 후 다음 (안 뜨면 건너뛰기)</li>
        </ol>
      </GuideStep>
      <GuideStep number={2} title="광고 계정 ID 확인">
        <p>
          비즈니스 설정 → 계정 → 광고 계정에서 광고 계정 ID(숫자)를 확인하고 복사해요.
        </p>
        <TipBox type="warning">
          카페24 자사몰과 연동된 광고 계정 ID여야 해요. act_를 제외한 숫자만 입력해주세요.
        </TipBox>
      </GuideStep>
      <GuideStep number={3} title="ID 입력">
        <p>
          복사한 광고 계정 ID를 '광고 계정 ID' 입력칸에 붙여넣고 저장해요. 저희가 자산 할당을 마친 뒤 승인해드리면 자동으로 연동돼요.
        </p>
      </GuideStep>
    </div>
  )
}

/** 메타 광고 간편 연동(FLB) 가이드 — 연동 방법 + 파트너 권한 부여 + (선택) 페이지 공유 */
function MetaAdsFLBGuide() {
  return (
    <div className="space-y-2">
      <GuideStep number={1} title="광고 계정 연동">
        <p>
          <strong>"연동하기"</strong> 버튼을 누르고 메타 로그인 후 연동할 광고
          계정을 선택해주세요.
        </p>
        <p>
          목록에 없는 광고 계정은 연동을 해제하지 말고{' '}
          <strong>"목록에 없는 광고 계정 추가"</strong> 버튼을 눌러 메타 권한 창에서 더 체크해주세요. 이미 체크된 항목은 풀지 말고 그대로 두세요.
        </p>
      </GuideStep>
      {/* 홈 '확인이 필요한 항목' 배너의 '가이드 보기'가 hash 로 이 스텝에 걸어 들어온다.
          scroll-mt 로 상단 고정 헤더에 가려지지 않게 여유를 둔다. */}
      <div id={FLB_PARTNER_GUIDE_ANCHOR_ID} className="scroll-mt-24">
        <GuideStep number={2} title="파트너 권한 부여">
          <p>
            원활한 광고 관리·지원을 위해 Insight Wave를 파트너로 등록하고 광고 계정을
            공유해주세요.
          </p>
          <ol className="list-decimal list-inside space-y-1.5 text-sm mt-2">
            <li>
              메타 비즈니스 설정 → 사용자 → 파트너에서 <strong>추가</strong> →{' '}
              <strong>파트너에게 자산에 대한 액세스 권한 부여</strong> 선택 → 파트너 비즈니스
              ID에 <CopyableId value="1081832460400655" /> 입력
            </li>
            <li>
              자산 및 권한 할당에서 <strong>광고 계정</strong> 선택 →{' '}
              <strong>전체 관리 권한</strong> 토글 켜기
            </li>
          </ol>
        </GuideStep>
      </div>
      {/* Facebook 페이지 공유 스텝 없음 — FLB(캠퍼스 pxon OAuth) 유저는 페이지 접근이
          pxon 자체 OAuth 로 해결되므로 안내하지 않는다. 직접 입력 가이드에만 유지. */}
    </div>
  )
}

/** 메타 광고 분석 가이드 (간편 연동용, 미사용이지만 보존) */
function MetaAdsGuide() {
  return (
    <div className="space-y-2">
      <GuideStep number={1} title="광고 계정 연동">
        <p>
          <strong>"연동하기"</strong> 버튼을 누르면 메타 로그인 후
          광고 계정을 선택할 수 있어요.
        </p>
        <TipBox type="info">
          광고 계정이 여러 개라면 데이터를 수집할 계정을 선택해요.
        </TipBox>
      </GuideStep>
    </div>
  )
}
